import Box from "@mui/material/Box";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Web3 from "web3";

import AllegianceInvitationPopup from "../../components/AllegianceInvitationPopup";
import AllegianceRequestPopup from "../../components/AllegianceRequestPopup";
import BackOverlay from "../../components/BackOverlay";
import ChatPanel from "../../components/ChatPanel";
import FamilyInvitationMemberPopup from "../../components/FamilyInvitationMemberPopup";
import InJailPopup from "../../components/InJailPopup";
import RateLimitPopup from "../../components/RateLimitPopup";
import HeaderV2 from "./HeaderV2";
import MobileSideBar from "./MobileSideBar";
import SideBar from "./SideBar";

import { getFamilyList } from "../../reducers/family.slice";
import { getJailedUntilAction } from "../../reducers/game.slice";
import {
  getBulletBalance,
  getCrateBalance,
  getHelperCreditBalance,
  getIsInJail,
  getOgNFTBalance,
  getProfileNames,
} from "../../reducers/profile.slice";

import config from "../../config/config";
import { JailWhiteListPath } from "../../constants/jail";
import { switchNetwork } from "../../helper/network";
import { AllegianceInvitationInfo } from "../../types/AllegianceInvitationInfo";
import { AllegianceRequestInfo } from "../../types/AllegianceRequestInfo";
import { FamilyInvitation } from "../../types/FamilyInvitation";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useAuth from "../../hook/useAuth";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";

import { useStyles } from "./index.styles";

const MainLayout = (props: React.PropsWithChildren) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLink = useLocation();
  const currentTime = useCurrentTime();
  const { wallet, connectWallet, disconnectWallet, account } = useWallet();

  useAuth(account);

  const { isInJail } = useAppSelector((state) => state.profile);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);
  const { jailedUntil } = useAppSelector((state) => state.game);

  const rateExceeded = useAppSelector((state) => state.auth.rateExceeded);

  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [openAllegianceInviteModal, setOpenAllegianceInviteModal] =
    useState(false);
  const [openAllegianceRequestModal, setOpenAllegianceRequestModal] =
    useState(false);

  const [invitationInfo, setInvitationInfo] = useState<FamilyInvitation>();
  const [allegianceInvitationInfo, setAllegianceInvitationInfo] =
    useState<AllegianceInvitationInfo>();

  const [allegianceRequestInfo, setAllegianceRequestInfo] =
    useState<AllegianceRequestInfo>();

  const [openChat, setOpenChat] = useState(false);
  const [openInJailPopup, setOpenInJailPopup] = useState(false);
  const [showMobileSideBar, toggleMobileSideBar] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const onConnectWallet = useCallback(() => {
    setOpenChat(false);
    connectWallet();
  }, [connectWallet]);

  useEffect(() => {
    if (wallet) {
      const checkNetwork = () => {
        const web3 = new Web3(wallet.provider);
        (window as any).provider = web3;

        (window as any).wallet = wallet;

        if (wallet.chains[0].id !== `0x${config.chainIdHex.toString(16)}`) {
          disconnectWallet();
          switchNetwork(wallet.provider)
            .then(() => {
              onConnectWallet();
            })
            .catch((error) => {
              disconnectWallet();
            });
        }
      };

      checkNetwork();
    }
  }, [wallet, dispatch, disconnectWallet, onConnectWallet]);

  useEffect(() => {
    if (currentLink.pathname !== "/" && isInJail) {
      navigate("/");
    }
    setOpenInJailPopup(isInJail);
  }, [isInJail, currentLink, navigate]);

  useEffect(() => {
    dispatch(getCrateBalance({ account }));
    dispatch(getOgNFTBalance({ account }));
    dispatch(getIsInJail({ account }));
    dispatch(getJailedUntilAction(account));
  }, [account, dispatch]);

  useEffect(() => {
    dispatch(getFamilyList());
    dispatch(getProfileNames());
  }, [dispatch, rateExceeded]);

  useEffect(() => {
    dispatch(getHelperCreditBalance({ account, signMsg, signature }));
    dispatch(getBulletBalance({ account, signMsg, signature }));
    updateBalance(account, signMsg, signature);
  }, [account, dispatch, signMsg, signature, updateBalance]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Prevent navigate to specific paths when in jail
  useEffect(() => {
    if (
      currentTime < jailedUntil &&
      JailWhiteListPath.indexOf(currentLink.pathname) === -1
    ) {
      navigate("/injail");
    }
  }, [currentTime, jailedUntil, navigate, currentLink.pathname]);

  return (
    <Box className={classes.body}>
      <HeaderV2
        setInvitationInfo={setInvitationInfo}
        setOpenInviteModal={setOpenInviteModal}
        setAllegianceInvitationInfo={setAllegianceInvitationInfo}
        setOpenAllegianceInviteModal={setOpenAllegianceInviteModal}
        setAllegianceRequestInfo={setAllegianceRequestInfo}
        setOpenAllegianceRequestModal={setOpenAllegianceRequestModal}
        toggleMobileSideBar={toggleMobileSideBar}
      />

      <Box className={classes.container}>
        {windowWidth >= 900 && <SideBar />}

        <Box className={classes.contentContainer}>{props.children}</Box>
      </Box>

      <MobileSideBar
        showMobileSideBar={showMobileSideBar}
        toggleMobileSideBar={toggleMobileSideBar}
        onConnectWallet={onConnectWallet}
      />

      <FamilyInvitationMemberPopup
        openInviteModal={openInviteModal}
        setOpenInviteModal={setOpenInviteModal}
        invitationInfo={invitationInfo}
      />

      <AllegianceInvitationPopup
        openInviteModal={openAllegianceInviteModal}
        setOpenInviteModal={setOpenAllegianceInviteModal}
        allegianceInvitationInfo={allegianceInvitationInfo}
      />

      <AllegianceRequestPopup
        openInviteModal={openAllegianceRequestModal}
        setOpenInviteModal={setOpenAllegianceRequestModal}
        allegianceRequestInfo={allegianceRequestInfo}
      />

      <RateLimitPopup openRateLimitPopup={rateExceeded} />

      <InJailPopup
        openInJailPopup={openInJailPopup}
        setOpenInJailPopup={setOpenInJailPopup}
      />

      <BackOverlay />

      <ChatPanel open={openChat} setOpen={(open) => setOpenChat(open)} />

      <ToastContainer />
    </Box>
  );
};

export default MainLayout;
